import React, { useEffect } from "react";
import PropTypes from "prop-types";
import SubscribeStreamButton from "core/components/SubscribeStreamButton";
import EpgButton from "core/components/EpgButton/EpgButton";
import ContentPlayer from "arena/components/ContentPlayer";
import Stack from "@mui/material/Stack";
import ContentBody from "arena/components/ContentBody";
import ErrorScreen from "core/components/ErrorScreen";
import ContentComments from "arena/components/ContentComments";
import Chat from "core/components/Chat";
import useWsLinkStatus from "core/hooks/useWsLinkStatus";
import { CloseRouteDialogButton } from "arena/components/RouteDialog";
import { ContentQuery, ContentStreamStatusQuery } from "arena/queries";
import { useApolloClient, useSubscription, useQuery } from "@apollo/client";
import analytics from "core/utils/analytics";
import { createEvent } from "core/utils/tracker";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import NoMatchScreen from "arena/components/NoMatchScreen";
import { useViewer } from "core/components/ViewerProvider";
import ContentHelmet from "arena/components/ContentHelmet";
// import SponsorChatTopBanner from "core/components/SponsorChatTopBanner";
import addAdToChat from "core/utils/addAdToChat";
import {
  ContentStreamStatusChangedSubscription,
  ContentViewersOnlineChangedSubscription,
} from "core/subscriptions";
import { useArena } from "core/components/ArenaProvider";
import Box from "@mui/material/Box";
import ContentLayout from "arena/components/ContentLayout";
import RelatedContent from "arena/components/RelatedContent";
import RelatedContentBannerSlot from "arena/components/RelatedContentBannerSlot";
import { useTranslation } from "react-i18next";
import useIsMobile from "arena/hooks/useIsMobile";

const STATUS_POLL_INTERVAL = 10 * 1000; // 10s

// const SITE = "SITE";
const STREAM = "STREAM";
const STREAM_ON_SITE = "STREAM_ON_SITE";

export default function Stream({ params: { contentId }, suspended }) {
  const { t } = useTranslation("arena");
  const isMobile = useIsMobile();
  const arena = useArena();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const client = useApolloClient();

  // const params = useParams();
  // const { contentId } = params;
  const productId = searchParams.get("productId");
  const startOffset = searchParams.get("startOffset")
    ? Number(searchParams.get("startOffset"))
    : 0;

  const { online: wsOnline } = useWsLinkStatus();

  const viewer = useViewer();

  const onCompleted = (data) => {
    if (data.content) {
      analytics.send({
        hitType: "pageview",
        page: location.pathname,
        title: data.content.title,
      });

      createEvent("page", "load");
    }
  };

  const { data, loading, error, refetch } = useQuery(ContentQuery, {
    variables: {
      contentId,
    },
    onCompleted,
  });

  useSubscription(ContentStreamStatusChangedSubscription, {
    skip: !process.browser,
    onData: ({ client, data }) => {
      if (data?.data?.contentStreamStatusChanged?.rejectModeration) {
        client.resetStore();
      }
    },
    variables: {
      id: contentId,
    },
  });

  useSubscription(ContentViewersOnlineChangedSubscription, {
    skip: !process.browser,
    variables: {
      id: contentId,
    },
  });

  /**
   * При отключении websocket
   * что бы не пропустить начало или конец стрима
   * запускаем polling
   */
  useQuery(ContentStreamStatusQuery, {
    skip: wsOnline || !data,
    // pollInterval: wsOnline ? 0 : STATUS_POLL_INTERVAL,
    pollInterval: STATUS_POLL_INTERVAL,
    variables: {
      contentId,
    },
  });

  const content = data?.content;

  const rejectModeration = content?.rejectModeration;

  useEffect(() => {
    if (rejectModeration) {
      client.resetStore();
    }
  }, [rejectModeration]);

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (!content && !loading) {
    return <NoMatchScreen />;
  }

  if (content && content.type !== STREAM) {
    return (
      <Navigate
        to={content.siteUrl.replace("stream", content.type.toLowerCase())}
      />
    );
  }

  const onChatAdv = (sponsorChatMessage) => {
    addAdToChat(content, client, sponsorChatMessage);
  };

  const samboData =
    content?.metadataFields?.reduce((acc, field) => {
      return {
        ...acc,
        [field.name]: field.value,
      };
    }, {}) || {};

  // Определяем какие табы активны
  const enabledTabs = [
    samboData.samboEventId && samboData.samboMatNumber && !isMobile, // EMBED
    !loading && !arena?.hideRelatedVideoOnStream, // RELATED_VIDEO
    content?.isChatEnabled, // CHAT
    content?.isCommentsEnabled, // COMMENTS
  ].filter(Boolean);

  const hasTabs = enabledTabs.length > 1;

  const relatedTabEnabled = !loading && !arena?.hideRelatedVideoOnStream;
  const showSidebarBanner =
    !isMobile &&
    content?.isAdvEnabled &&
    relatedTabEnabled &&
    content?.rightholder?.id;

  const sidebarHeader = showSidebarBanner ? (
    <RelatedContentBannerSlot
      contentId={contentId}
      rightholderId={content.rightholder.id}
      place={STREAM}
      variant="sidebar"
    />
  ) : null;

  const tabsSchema = {
    EMBED: {
      label: t("video.tabsSchema.embed"),
      component:
        samboData.samboEventId && samboData.samboMatNumber ? (
          <iframe
            src={`https://live.sambo.sport/scheduled-fights/${samboData.samboEventId}/${samboData.samboMatNumber}`}
            style={{ width: "100%", height: "100%", border: "none" }}
            allow="autoplay; fullscreen"
          />
        ) : null,
      enabled: samboData.samboEventId && samboData.samboMatNumber && !isMobile,
    },
    RELATED_VIDEO: {
      label: t("video.tabsSchema.relatedVideo"),
      component: (
        <RelatedContent
          type="VIDEO"
          limit={6}
          selectedContentId={contentId}
          relatedContentId={contentId}
          productId={productId}
          contentId={contentId}
          rightholderId={content?.rightholder?.id}
          place={STREAM}
          isAdvEnabled={!isMobile && content?.isAdvEnabled}
        />
      ),
      enabled: relatedTabEnabled,
    },
    CHAT: {
      label: t("video.tabsSchema.chat"),
      component: (
        <Chat
          content={content}
          loading={loading}
          viewer={viewer}
          hasTabs={hasTabs}
        />
      ),
      enabled: content?.isChatEnabled,
    },
    COMMENTS: {
      label: t("video.tabsSchema.comments"),
      component: <ContentComments contentId={contentId} />,
      enabled: content?.isCommentsEnabled,
    },
  };

  return (
    <Box data-testid="Stream" key={contentId}>
      <ContentHelmet content={content} arena={arena} />

      <ContentLayout
        tabsSchema={content ? tabsSchema : {}}
        sidebarHeader={sidebarHeader}
      >
        <ContentPlayer
          suspended={suspended}
          sponsorCornerBannerPlace={STREAM_ON_SITE}
          sponsorInStreamBannerPlace={STREAM_ON_SITE}
          sponsorPlayerLogoPlace={STREAM_ON_SITE}
          sponsorPlayerButtonPlace={STREAM_ON_SITE}
          sponsorVideoPlace={STREAM_ON_SITE}
          sponsorChatMessagePlace={STREAM_ON_SITE}
          hideTitle
          autoPlay
          content={content}
          loading={loading}
          subscribeButton={
            content && (
              <Stack direction="row" spacing={1} alignItems="center">
                <SubscribeStreamButton content={content} />
                <EpgButton contentId={contentId} variant="outlined" />
              </Stack>
            )
          }
          onChatAdv={onChatAdv}
          refetch={refetch}
          startOffset={startOffset}
          // chatEnabled={chatEnabled}
          closeRouteDialogButton={<CloseRouteDialogButton />}
        />

        <Box
          sx={{
            position: { xs: "relative", md: "static" },
            // minHeight: { xs: 500, md: "auto" },
            display: { xs: "flex", md: "block" },
            flexDirection: "column",
            gap: { xs: 2, md: 0 },
          }}
        >
          <ContentBody content={content} loading={loading} />

          {content?.isCommentsEnabled && isMobile && (
            <ContentComments contentId={contentId} isMobile />
          )}

          {content?.isChatEnabled && content?.chatRoom?.enabled && isMobile && (
            <Box sx={{ mt: -1.5 }}>
              <Chat
                content={content}
                loading={loading}
                viewer={viewer}
                isMobile
                hasTabs={false}
              />
            </Box>
          )}

          {isMobile && samboData.samboEventId && samboData.samboMatNumber && (
            <Box sx={{ padding: "12px 8px 8px 12px" }}>
              <iframe
                src={`https://live.sambo.sport/scheduled-fights/${samboData.samboEventId}/${samboData.samboMatNumber}`}
                style={{
                  width: "100%",
                  height: "600px",
                  border: "none",
                }}
                allow="autoplay; fullscreen"
              />
            </Box>
          )}
        </Box>
      </ContentLayout>

      {isMobile && !loading && !arena?.hideRelatedVideoOnStream && (
        <RelatedContent
          type="VIDEO"
          limit={6}
          selectedContentId={contentId}
          relatedContentId={contentId}
          productId={productId}
          isMobile
          title={t("video.tabsSchema.relatedVideo")}
          contentId={contentId}
          rightholderId={content?.rightholder?.id}
          place={STREAM}
          isAdvEnabled={content?.isAdvEnabled}
        />
      )}

      {/* {chatEnabled && (
            <div className={classes.chat}>
              <div className={classes.chatInner}>
                {content?.isAdvEnabled && (
                  <SponsorChatTopBanner
                    contentId={contentId}
                    rightholderId={content.rightholder.id}
                    place={SITE}
                  />
                )}

                <Chat content={content} loading={loading} viewer={viewer} />
              </div>
            </div>
          )} */}
    </Box>
  );
}

Stream.propTypes = {
  suspended: PropTypes.bool,
  params: PropTypes.shape({
    contentId: PropTypes.string.isRequired,
  }),
};
